import { useState, type ReactNode } from 'react';
import { MessageCircleQuestion, ShieldAlert, Sparkles, TerminalSquare } from 'lucide-react';
import { toast } from 'sonner';
import { APPROVAL_NOTE_MAX_CHARS, DECISION_LABELS, isOpenApproval, type ApprovalExplanation, type ApprovalRequest, type ApproveDecision } from '@shared/approval-request';
import type { FactoryAnswer, FactoryQuestions } from '@shared/factory-questions';
import { AppAlert } from '@/components/AppAlert';
import { ExternalLink } from '@/components/ExternalLink';
import { FormField } from '@/components/FormField';
import { LoadError } from '@/components/LoadError';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { useLoader } from '@/hooks/use-loader';
import { usePermission } from '@/hooks/usePermission';
import { describeApiError } from '@/lib/api-errors';
import { useCurrentProject } from '@/lib/current-project';
import { formatRelative } from '@/lib/formatters';
import { answerTaskApprovalQuestion, approveTaskApprovalRequest, declineTaskApprovalRequest, denyTaskApprovalRequest, explainTaskApprovalRequest, getTaskApprovalRequests } from '@/lib/tasks-api';
import { approvalStatusMeta } from './approval-status';
import { FactoryQuestionsCard } from './FactoryQuestionsCard';

/**
 * SF-T-177: the permission requests a task's run waits on in T3 Code (shared/approval-request.ts). Each
 * open one shows the command, the agent's last message before it and the AI's explanation; it's approved
 * here (SF-T-225: Approve or Approve for session, RUNS_START, sent to T3 by the run) or in T3 Code, or
 * declined here with a message for the agent (RUNS_START): T3's own Decline, where the agent carries on in
 * the same turn, or a Deny, which restarts its waiting turn. Answered ones fold to a line.
 *
 * The agent's own T3 questions (kind `question`) show here too, as a question card answered from the task
 * (RUNS_START): the run sends the answers through T3 and the agent carries on. Both link straight to the
 * thread in T3 Code, in a browser and in the T3 Code app.
 */

export function TaskApprovalRequestsSection({
  taskId,
  version,
  preview,
}: {
  taskId: string;
  /** Changes with the task (its updated_at and open count), so the list reads again. */
  version: string;
  /** Show these instead of loading the task's (the /dev/ui gallery). */
  preview?: ApprovalRequest[];
}) {
  const projectId = useCurrentProject()?.id;
  const loader = useLoader((signal) => getTaskApprovalRequests(taskId, { signal }), [taskId, version], {
    key: `${projectId ?? 'none'}:task-approvals:${taskId}:${version}`,
    staleMs: 30_000,
    enabled: !preview,
    // The explanation is made in the background, and a Deny settles once the machine answers: look again until they have.
    pollMs: 4_000,
    pollWhile: (res) =>
      !!res?.data?.approval_requests.some(
        (r) =>
          (r.kind === 'approval' && isOpenApproval(r) && !r.explanation && !r.explanation_error) || r.status === 'denying' || r.status === 'answering' || r.status === 'approving'
      ),
    pollLimit: 20,
  });
  const loaded = preview ?? loader.data?.data?.approval_requests ?? [];
  const [changed, setChanged] = useState<Record<string, ApprovalRequest>>({});
  const list = loaded.map((r) => (changed[r.id] && changed[r.id]!.updated_at >= r.updated_at ? changed[r.id]! : r));
  const replace = (r: ApprovalRequest) => setChanged((c) => ({ ...c, [r.id]: r }));
  const failed = !preview && loader.data?.error ? describeApiError(loader.data, 'Try again in a moment.') : null;
  if (!list.length && !failed) return null;
  const open = list.filter(isOpenApproval);
  const answered = list.filter((r) => !isOpenApproval(r));
  const questions = list.some((r) => r.kind === 'question');
  const heading = open.length
    ? open.every((r) => r.kind === 'question')
      ? 'T3 question'
      : 'Approval request'
    : questions
      ? 'Approvals and questions from T3 Code'
      : 'Approval requests';
  return (
    <section className="space-y-2" aria-label="Approval requests">
      <h3 className="text-sm font-semibold">{heading}</h3>
      {failed ? (
        <LoadError compact title={list.length ? "Couldn't refresh the approval requests: these may be out of date" : "Couldn't load the approval requests"} error={failed} onRetry={loader.reload} />
      ) : null}
      {open.map((r) => (
        <ApprovalRequestCard key={r.id} taskId={taskId} request={r} onChanged={replace} />
      ))}
      {answered.length ? (
        <ul className="space-y-1">
          {answered.map((r) => (
            <ApprovalRequestLine key={r.id} request={r} />
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/** An answered request, as one line. */
export function ApprovalRequestLine({ request }: { request: ApprovalRequest }) {
  const question = request.kind === 'question';
  const asked = request.questions?.[0];
  const first = question
    ? asked
      ? `${asked.header ? `${asked.header}: ` : ''}${asked.question}`
      : (request.context ?? 'A question from the agent')
    : (request.command?.split('\n')[0] ?? 'A permission request');
  const declined = request.decision === 'decline';
  const how =
    request.status === 'denied' || request.status === 'denying' || (request.status === 'approving' && declined)
      ? `${declined ? 'declined' : 'denied'} by ${request.decided_by?.name ?? 'someone'}`
      : request.status === 'answered' || request.status === 'answering'
        ? `answered by ${request.decided_by?.name ?? 'someone'}`
        : request.status === 'approved' || request.status === 'approving'
          ? `${request.decision === 'acceptForSession' ? 'approved for the session' : 'approved'} by ${request.decided_by?.name ?? 'someone'}`
        : request.stale && !request.resolved_at
          ? 'the run moved on'
          : 'answered in T3 Code';
  return (
    <li className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
      <StatusBadge size="sm" meta={approvalStatusMeta(request)} />
      {question ? (
        <span className="min-w-0 truncate" data-hint={first}>
          {first}
        </span>
      ) : (
        <code className="min-w-0 truncate font-mono" data-hint={request.command ?? undefined}>
          {first}
        </code>
      )}
      <span className="shrink-0">
        {request.run_number ? `R-${request.run_number}, ` : ''}
        {how} {formatRelative(request.decided_at ?? request.resolved_at ?? request.updated_at)}
      </span>
    </li>
  );
}

/** An open request: what it wants to run, why, the AI's read of it, and how to answer it. A T3 question is T3QuestionCard. */
export function ApprovalRequestCard({ taskId, request, onChanged }: { taskId: string; request: ApprovalRequest; onChanged: (r: ApprovalRequest) => void }) {
  if (request.kind === 'question') return <T3QuestionCard taskId={taskId} request={request} onChanged={onChanged} />;
  return (
    <div className="space-y-3 rounded-md border border-warning-border bg-card p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge size="sm" meta={approvalStatusMeta(request)} />
        <span className="text-xs text-muted-foreground">
          {request.run_number ? `R-${request.run_number}'s agent` : 'The agent'} asked {formatRelative(request.created_at)}
          {request.machine_name ? ` on ${request.machine_name}` : ''}
        </span>
      </div>
      <section className="space-y-1.5">
        <h4 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <TerminalSquare className="size-3.5" aria-hidden />
          It wants to run{request.item_type ? ` (${request.item_type.replace(/_/g, ' ')})` : ''}
        </h4>
        {request.command ? (
          <pre className="max-h-64 overflow-auto rounded-md bg-muted p-2 font-mono text-xs whitespace-pre-wrap break-all">{request.command}</pre>
        ) : (
          <p className="text-xs text-muted-foreground">The factory couldn't tell what for: look at the thread in T3 Code.</p>
        )}
      </section>
      {request.context ? (
        <section className="space-y-1.5">
          <h4 className="text-xs font-medium text-muted-foreground">What it said just before</h4>
          <blockquote className="max-h-48 overflow-auto border-l-2 pl-3 whitespace-pre-wrap break-words">{request.context}</blockquote>
        </section>
      ) : null}
      <ApprovalExplanationBox taskId={taskId} request={request} onChanged={onChanged} />
      {request.error ? (
        <AppAlert compact tone="critical">
          {request.error}
        </AppAlert>
      ) : null}
      <ApprovalActions taskId={taskId} request={request} onChanged={onChanged} />
    </div>
  );
}

/** The thread in T3 Code: in a browser (when T3 is reachable beyond its machine) and in the T3 Code app. */
export function T3ThreadLinks({ request }: { request: Pick<ApprovalRequest, 't3_url' | 't3_app_url'> }) {
  if (!request.t3_url && !request.t3_app_url) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-3 text-xs max-md:min-h-11">
      {request.t3_url ? (
        <ExternalLink href={request.t3_url} strong>
          Open the thread
        </ExternalLink>
      ) : null}
      {request.t3_app_url ? (
        <a href={request.t3_app_url} className="font-medium text-primary hover:underline" data-hint="Opens the thread in the T3 Code app, on a machine that has it">
          Open in the T3 app
        </a>
      ) : null}
    </span>
  );
}

/** T3's questions as the question card shows them. */
const asFactoryQuestions = (request: ApprovalRequest): FactoryQuestions => ({
  ask: null,
  questions: (request.questions ?? []).map((q) => ({
    id: q.id,
    header: q.header || undefined,
    question: q.question,
    options: q.options.map((o) => ({ label: o.label, description: o.description || undefined })),
    multiSelect: Boolean(q.multiSelect),
  })),
});

/** What the person sent, per question, for the answered card: each value read back as its option's label. */
function givenAnswers(request: ApprovalRequest): (string | null)[] | null {
  if (!request.answers || !request.questions) return null;
  return request.questions.map((q) => {
    const a = request.answers![q.id];
    const values = Array.isArray(a) ? a : a ? [a] : [];
    return values.length ? values.map((v) => q.options.find((o) => (o.value ?? o.label) === v)?.label ?? v).join('; ') : null;
  });
}

/** SF-T-177: the agent's own T3 question, answered here (RUNS_START) or in T3 Code. */
export function T3QuestionCard({ taskId, request, onChanged }: { taskId: string; request: ApprovalRequest; onChanged: (r: ApprovalRequest) => void }) {
  const mayAnswer = usePermission('RUNS_START');
  const [busy, setBusy] = useState(false);
  const from = `${request.run_number ? `R-${request.run_number}'s agent` : 'the agent'} in T3 Code`;
  const sending = request.status === 'answering' && !request.deny_retryable;
  const canAnswer = mayAnswer && (request.status === 'pending' || (request.status === 'answering' && request.deny_retryable));
  const send = async (answers: Record<string, FactoryAnswer | undefined>) => {
    setBusy(true);
    const res = await answerTaskApprovalQuestion(taskId, request.id, answers);
    setBusy(false);
    if (res.data) {
      onChanged(res.data.approval_request);
      toast.success('Sent: the agent carries on with your answers');
      return;
    }
    toast.error(describeApiError(res, "Couldn't send the answers"));
  };
  const where = request.machine_name ? ` on ${request.machine_name}` : '';
  return (
    <div className="space-y-3 rounded-md border border-warning-border bg-card p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge size="sm" meta={approvalStatusMeta(request)} />
        <span className="text-xs text-muted-foreground">
          {request.run_number ? `R-${request.run_number}'s agent` : 'The agent'} asked {formatRelative(request.created_at)}
          {where}
        </span>
      </div>
      {request.questions?.length ? (
        <FactoryQuestionsCard
          questions={asFactoryQuestions(request)}
          from={from}
          answered={request.status === 'answering' && !request.deny_retryable}
          answers={request.status === 'answering' ? givenAnswers(request) : null}
          onAnswers={canAnswer ? (answers) => void send(answers) : undefined}
          sending={busy}
        />
      ) : (
        <section className="space-y-1.5">
          <h4 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <MessageCircleQuestion className="size-3.5" aria-hidden />
            It asks
          </h4>
          <p className="whitespace-pre-wrap break-words">{request.context ?? 'A question the factory couldn\'t read.'}</p>
          <p className="text-xs text-muted-foreground">The factory couldn't read its options: answer it in T3 Code.</p>
        </section>
      )}
      {request.error ? (
        <AppAlert compact tone="critical">
          {request.error}
        </AppAlert>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-auto text-xs text-muted-foreground">
          {sending ? (
            <span className="inline-flex items-center gap-2">
              <Spinner size="xs" />
              Sending your answers to the agent…
            </span>
          ) : request.deny_retryable ? (
            "The last answers didn't finish: send them again, or answer in T3 Code."
          ) : (
            `Or answer it in T3 Code${where}.`
          )}
        </span>
        <T3ThreadLinks request={request} />
      </div>
    </div>
  );
}

/** The AI's text with `backticked` names as inline code. */
function WithCode({ text }: { text: string }) {
  return (
    <>
      {text.split(/(`[^`\n]+`)/).map((part, i) =>
        part.startsWith('`') && part.endsWith('`') && part.length > 2 ? (
          <code key={i} className="rounded-sm bg-muted px-1 font-mono text-[0.9em] break-all">
            {part.slice(1, -1)}
          </code>
        ) : (
          part
        )
      )}
    </>
  );
}

function ApprovalExplanationBox({ taskId, request, onChanged }: { taskId: string; request: ApprovalRequest; onChanged: (r: ApprovalRequest) => void }) {
  const mayAnswer = usePermission('RUNS_START');
  const [explaining, setExplaining] = useState(false);
  const explain = async () => {
    setExplaining(true);
    const res = await explainTaskApprovalRequest(taskId, request.id);
    setExplaining(false);
    if (res.data) onChanged(res.data.approval_request);
    else toast.error(describeApiError(res, "Couldn't explain the request"));
  };
  const again =
    mayAnswer && request.status === 'pending' ? (
      <Button type="button" size="xs" variant="outline" className="max-md:h-11" loading={explaining} loadingText="Explaining…" onClick={() => void explain()}>
        Explain again
      </Button>
    ) : null;
  return <ApprovalExplanationView explanation={request.explanation} error={request.explanation_error} again={again} />;
}

/**
 * The AI's read of a permission request (approval-explain): what it's trying to do, what it touches, and what
 * to look at twice; "Explaining…" until it's made, or why it couldn't be. SF-T-225: a chat's approval card
 * shows it too. `again` is an Explain again button, where there's one.
 */
export function ApprovalExplanationView({ explanation: e, error, again }: { explanation: ApprovalExplanation | null; error: string | null; again?: ReactNode }) {
  return (
    <section className="space-y-2 rounded-md border border-ai-border bg-ai-soft p-3">
      <h4 className="flex items-center gap-1.5 text-xs font-medium text-ai-strong">
        <Sparkles className="size-3.5" aria-hidden />
        What it's trying to do
      </h4>
      {e ? (
        <>
          <p className="leading-relaxed">
            <WithCode text={e.summary} />
          </p>
          {e.effects.length ? (
            <ul className="list-disc space-y-1 pl-4 text-xs">
              {e.effects.map((c, i) => (
                <li key={i} className="break-words">
                  <WithCode text={c} />
                </li>
              ))}
            </ul>
          ) : null}
          {e.risks.length ? (
            <AppAlert compact tone="warning" icon={<ShieldAlert />} title="Look twice at">
              <ul className="list-disc space-y-1 pl-4">
                {e.risks.map((r, i) => (
                  <li key={i} className="break-words">
                    <WithCode text={r} />
                  </li>
                ))}
              </ul>
            </AppAlert>
          ) : (
            <p className="text-xs text-muted-foreground">Nothing in it stood out as risky.</p>
          )}
          <div className="flex items-center justify-between gap-2">
            <span className="text-3xs text-muted-foreground">Written by AI from the request and the agent's words; check it against the command.</span>
            {again}
          </div>
        </>
      ) : error ? (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">{error}</p>
          {again}
        </div>
      ) : (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Spinner size="xs" />
          Explaining the request…
        </p>
      )}
    </section>
  );
}

/**
 * SF-T-225: Approve and Approve for session, as T3 offers them for the request; the one being sent shows its
 * spinner and the rest wait. Shared by the task's card and a chat's.
 */
export function ApproveButtons({
  decisions,
  sending,
  disabled,
  onApprove,
}: {
  decisions: readonly ApproveDecision[];
  /** The decision on its way, if any. */
  sending: ApproveDecision | null;
  disabled?: boolean;
  onApprove: (decision: ApproveDecision) => void;
}) {
  return (
    <>
      {decisions.map((d) => (
        <Button
          key={d}
          type="button"
          size="xs"
          variant={d === 'accept' ? 'default' : 'outline'}
          className="max-md:h-11"
          disabled={disabled || (sending !== null && sending !== d)}
          loading={sending === d}
          loadingText="Approving…"
          data-hint={d === 'acceptForSession' ? "Approve this, and don't ask again for the same kind of request in this session" : 'Approve this request once'}
          onClick={() => onApprove(d)}
        >
          {DECISION_LABELS[d].button}
        </Button>
      ))}
    </>
  );
}

/**
 * Approve or Approve for session (SF-T-225, as T3 offers them), Deny… with a message for the agent
 * (RUNS_START), or open the thread in T3 Code to answer it there. A request the machine couldn't read from
 * T3 (an older agent) is approved in T3 Code only.
 */
export function ApprovalActions({ taskId, request, onChanged }: { taskId: string; request: ApprovalRequest; onChanged: (r: ApprovalRequest) => void }) {
  const mayAnswer = usePermission('RUNS_START');
  const [denying, setDenying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [approving, setApproving] = useState<ApproveDecision | null>(null);
  const [note, setNote] = useState('');
  const where = request.machine_name ? ` on ${request.machine_name}` : '';
  const approvable = request.approve_decisions.length > 0;
  const approveHint = approvable
    ? `Or answer it in T3 Code${where}.`
    : request.t3_thread_id
      ? `Approve it in T3 Code${where}: the thread is ${request.t3_thread_id}.`
      : `Approve it in T3 Code${where}.`;

  if (request.status === 'denying' && !request.deny_retryable) {
    return (
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <Spinner size="xs" />
        Restarting the agent's turn with your message…
      </p>
    );
  }
  if (request.status === 'approving' && !request.deny_retryable) {
    return (
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <Spinner size="xs" />
        {request.decision === 'decline' ? 'Sending your Decline to the agent…' : 'Sending your approval to the agent…'}
      </p>
    );
  }

  /** SF-T-225: T3's own Decline: the agent hears no (with the message) and carries on in the same turn. */
  const decline = async () => {
    setBusy(true);
    const res = await declineTaskApprovalRequest(taskId, request.id, note);
    setBusy(false);
    if (res.data) {
      onChanged(res.data.approval_request);
      setDenying(false);
      toast.success('Declined: the agent carries on without it');
      return;
    }
    toast.error(describeApiError(res, "Couldn't decline the request"));
  };

  const approve = async (decision: ApproveDecision) => {
    setApproving(decision);
    const res = await approveTaskApprovalRequest(taskId, request.id, decision);
    setApproving(null);
    if (res.data) {
      onChanged(res.data.approval_request);
      toast.success(decision === 'acceptForSession' ? 'Approved for the session: the agent carries on' : 'Approved: the agent carries on');
      return;
    }
    toast.error(describeApiError(res, "Couldn't approve the request"));
  };

  const deny = async () => {
    setBusy(true);
    const res = await denyTaskApprovalRequest(taskId, request.id, note);
    setBusy(false);
    if (res.data) {
      onChanged(res.data.approval_request);
      setDenying(false);
      toast.success("Denied: the agent's turn restarts with your message");
      return;
    }
    toast.error(describeApiError(res, "Couldn't deny the request"));
  };

  if (denying) {
    return (
      <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-end">
        <FormField label="Message for the agent" className="min-w-0 flex-1">
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={APPROVAL_NOTE_MAX_CHARS}
            rows={2}
            placeholder="Why not, or what to do instead"
          />
        </FormField>
        <div className="flex shrink-0 flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" className="max-md:h-11" disabled={busy} onClick={() => setDenying(false)}>
            Cancel
          </Button>
          {request.can_decline ? (
            <>
              <Button
                type="button"
                variant="outline"
                className="max-md:h-11"
                disabled={busy}
                data-hint="Stop the agent's turn and start a new one with your message"
                onClick={() => void deny()}
              >
                Restart its turn instead
              </Button>
              <Button
                type="button"
                variant="destructive"
                className="max-md:h-11"
                loading={busy}
                loadingText="Declining…"
                data-hint="Tell the agent no, with your message: it carries on in the same turn"
                onClick={() => void decline()}
              >
                Decline
              </Button>
            </>
          ) : (
            <Button type="button" variant="destructive" className="max-md:h-11" loading={busy} loadingText="Denying…" onClick={() => void deny()}>
              Deny and restart its turn
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="mr-auto text-xs text-muted-foreground">
        {request.deny_retryable ? "The last answer didn't finish: send it again, or answer it in T3 Code. " : ''}
        {approveHint}
      </span>
      <T3ThreadLinks request={request} />
      {mayAnswer ? (
        <div className="flex flex-wrap items-center gap-2">
          {approvable ? <ApproveButtons decisions={request.approve_decisions} sending={approving} onApprove={(d) => void approve(d)} /> : null}
          <Button type="button" size="xs" variant="outline" className="max-md:h-11" disabled={approving !== null} onClick={() => setDenying(true)}>
            {request.can_decline ? 'Decline…' : 'Deny…'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
